import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DollarSign, TrendingUp, TrendingDown, FileText, 
  Download, Filter, Search, CheckCircle, Clock, AlertCircle 
} from 'lucide-react';

const Buchhaltung = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data
  const rechnungen = [
    {
      id: 1,
      aktenzeichen: 'KH-091125-576',
      kunde: 'Max Mustermann',
      betrag: 450.00,
      datum: '09.11.2025',
      faellig: '23.11.2025',
      status: 'offen',
      typ: 'Haftpflichtgutachten'
    },
    {
      id: 2,
      aktenzeichen: 'KH-081125-575',
      kunde: 'Anna Schmidt',
      betrag: 520.00,
      datum: '08.11.2025',
      faellig: '22.11.2025',
      status: 'bezahlt',
      bezahltAm: '15.11.2025',
      typ: 'Haftpflichtgutachten'
    },
    {
      id: 3,
      aktenzeichen: 'KH-071125-029',
      kunde: 'Qualid El Kadiri',
      betrag: 380.00,
      datum: '07.11.2025',
      faellig: '21.11.2025',
      status: 'ueberfaellig',
      typ: 'Haftpflichtgutachten'
    },
    {
      id: 4,
      aktenzeichen: 'KH-291025-059',
      kunde: 'Ouafa Ouyachou',
      betrag: 650.00,
      datum: '29.10.2025',
      faellig: '12.11.2025',
      status: 'bezahlt',
      bezahltAm: '10.11.2025',
      typ: 'Haftpflichtgutachten'
    },
    {
      id: 5,
      aktenzeichen: 'KH-LE-071125',
      kunde: 'Lara Eman',
      betrag: 420.00,
      datum: '07.11.2025',
      faellig: '21.11.2025',
      status: 'offen',
      typ: 'Haftpflichtgutachten'
    }
  ];

  // Statistiken berechnen
  const gesamtOffen = rechnungen
    .filter(r => r.status === 'offen' || r.status === 'ueberfaellig')
    .reduce((sum, r) => sum + r.betrag, 0);
  
  const gesamtBezahlt = rechnungen
    .filter(r => r.status === 'bezahlt')
    .reduce((sum, r) => sum + r.betrag, 0);
  
  const ueberfaellig = rechnungen.filter(r => r.status === 'ueberfaellig').length;

  // Filter anwenden
  const filteredRechnungen = rechnungen.filter(rechnung => {
    const matchesSearch = rechnung.aktenzeichen.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rechnung.kunde.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || rechnung.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusConfig = (status) => {
    switch(status) {
      case 'bezahlt':
        return { 
          label: 'Bezahlt', 
          color: 'bg-green-100 text-green-700 border-green-200',
          icon: CheckCircle 
        };
      case 'offen':
        return { 
          label: 'Offen', 
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          icon: Clock 
        };
      case 'ueberfaellig':
        return { 
          label: 'Überfällig', 
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: AlertCircle 
        };
      default:
        return { 
          label: status, 
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          icon: FileText 
        };
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Buchhaltung</h1>
            <p className="text-gray-500 mt-1">Rechnungen und Zahlungen verwalten</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bezahlt</p>
                  <p className="text-2xl font-bold text-gray-900">€ {gesamtBezahlt.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Offen</p>
                  <p className="text-2xl font-bold text-gray-900">€ {gesamtOffen.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Überfällig</p>
                  <p className="text-2xl font-bold text-gray-900">{ueberfaellig}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter und Suche */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <CardTitle>Rechnungsübersicht</CardTitle>
              <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full md:w-64"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Alle Status</option>
                  <option value="offen">Offen</option>
                  <option value="bezahlt">Bezahlt</option>
                  <option value="ueberfaellig">Überfällig</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Aktenzeichen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Betrag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Fällig
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRechnungen.map((rechnung) => {
                    const statusConfig = getStatusConfig(rechnung.status);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr key={rechnung.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {rechnung.aktenzeichen}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">{rechnung.kunde}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{rechnung.typ}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-gray-900">
                            € {rechnung.betrag.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{rechnung.datum}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{rechnung.faellig}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded border ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            <span className="text-xs font-medium">{statusConfig.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => alert(`Rechnung ${rechnung.aktenzeichen} öffnen`)}
                            >
                              Ansehen
                            </Button>
                            {rechnung.status !== 'bezahlt' && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => alert(`Zahlung für ${rechnung.aktenzeichen} erfassen`)}
                              >
                                Zahlung
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredRechnungen.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Keine Rechnungen gefunden</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Buchhaltung;
